import * as React from 'react';
import { Contrast, Languages, LogOut, Moon, Sun, UserRound } from 'lucide-react';
import { cn } from '../../lib/utils';
import { nextTheme, type ThemeName } from '../../lib/theme';
import { buttonVariants } from './button';
import { IconButton } from './icon-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';

/*
 * Shell controls shared by every console top bar. Strings are props with
 * English defaults; apps pass `useShellLabels()` from `@cguard/i18n`.
 */

export interface ThemeToggleLabels {
  /** Accessible name of the button. */
  toggle: string;
  /** Tooltip naming the current theme. */
  current: (theme: ThemeName) => string;
}

const THEME_NAMES: Record<ThemeName, string> = {
  light: 'Light',
  dark: 'Dark',
  hc: 'High contrast',
};

const defaultThemeLabels: ThemeToggleLabels = {
  toggle: 'Change theme',
  current: (theme) => `Theme: ${THEME_NAMES[theme]}`,
};

export interface ThemeToggleProps {
  theme: ThemeName;
  /** Called with the next theme in the shared order: dark → high contrast → light. */
  onThemeChange: (theme: ThemeName) => void;
  labels?: Partial<ThemeToggleLabels>;
  className?: string;
}

/** Icon button that shows the current theme and cycles to the next one. */
function ThemeToggle({
  theme,
  onThemeChange,
  labels,
  className,
}: ThemeToggleProps): React.JSX.Element {
  const text = { ...defaultThemeLabels, ...labels };
  const Icon = theme === 'dark' ? Moon : theme === 'hc' ? Contrast : Sun;
  return (
    <IconButton
      aria-label={text.toggle}
      title={text.current(theme)}
      icon={<Icon aria-hidden="true" />}
      onClick={() => onThemeChange(nextTheme(theme))}
      className={className}
    />
  );
}

export interface LanguageToggleLabels {
  /** Accessible name of the button. */
  toggle: string;
  /** Tooltip naming the current language. */
  current: (locale: string) => string;
}

const LANGUAGE_NAMES: Record<string, string> = { en: 'English', ar: 'العربية' };

const defaultLanguageLabels: LanguageToggleLabels = {
  toggle: 'Change language',
  current: (locale) => `Language: ${LANGUAGE_NAMES[locale] ?? locale}`,
};

export interface LanguageToggleProps {
  locale: string;
  /** Called with the next locale in `locales`. */
  onLocaleChange: (locale: string) => void;
  /** Locales to cycle through; defaults to English and Arabic. */
  locales?: readonly string[];
  labels?: Partial<LanguageToggleLabels>;
  className?: string;
}

/** Icon button that switches to the next supported language. */
function LanguageToggle({
  locale,
  onLocaleChange,
  locales = ['en', 'ar'],
  labels,
  className,
}: LanguageToggleProps): React.JSX.Element {
  const text = { ...defaultLanguageLabels, ...labels };
  const next = locales[(locales.indexOf(locale) + 1) % locales.length] ?? locale;
  return (
    <IconButton
      aria-label={text.toggle}
      title={text.current(locale)}
      icon={<Languages aria-hidden="true" />}
      onClick={() => onLocaleChange(next)}
      className={className}
    />
  );
}

export interface AccountMenuLabels {
  /** Accessible name of the menu button. */
  menu: string;
  signOut: string;
}

const defaultAccountLabels: AccountMenuLabels = {
  menu: 'Account menu',
  signOut: 'Sign out',
};

export interface AccountMenuProps {
  /** Signed-in user's display name; shown on the button from `sm` up. */
  name?: string;
  /** Role or tenant line under the name inside the menu. */
  role?: string;
  onSignOut: () => void;
  labels?: Partial<AccountMenuLabels>;
  /** Extra `DropdownMenuItem`s placed above Sign out. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * Account button with a menu (Base UI Menu: arrow keys, Escape, focus
 * return). Sign out always lives here, so every console signs out the same way.
 */
function AccountMenu({
  name,
  role,
  onSignOut,
  labels,
  children,
  className,
}: AccountMenuProps): React.JSX.Element {
  const text = { ...defaultAccountLabels, ...labels };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={name ? `${name} · ${text.menu}` : text.menu}
        className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'gap-2', className)}
      >
        <UserRound className="size-4" aria-hidden="true" />
        {name ? <span className="hidden max-w-40 truncate sm:inline">{name}</span> : null}
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {name ? (
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="truncate text-sm text-[var(--color-foreground)]">{name}</span>
              {role ? <span className="truncate">{role}</span> : null}
            </DropdownMenuLabel>
          </DropdownMenuGroup>
        ) : null}
        {name ? <DropdownMenuSeparator /> : null}
        {children}
        {children ? <DropdownMenuSeparator /> : null}
        <DropdownMenuItem onClick={onSignOut}>
          <LogOut aria-hidden="true" />
          {text.signOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { AccountMenu, LanguageToggle, ThemeToggle };
